import PaginationButtons from "@/components/PaginationButtons";
import { SimpleFilter } from "@/components/simple-filter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTimeFromISO } from "@/lib/formatters";
import { PagyMetadata, SubscriptionStatus } from "@/types";
import { router } from "@inertiajs/react";
import { MoreHorizontal } from "lucide-react";
import AdminLayout from "./layout";

interface User {
  id: string;
  email: string;
  name: string;
  is_admin: boolean;
  created_at: string;
  subscription_status: SubscriptionStatus | null;
  stripe_customer_id: string | null;
  stripe_customer_url: string | null;
  subscription_key: string | null;
  email_verified: boolean;
}

function None() {
  return <span className="text-muted-foreground">--</span>;
}

function UserDetailsSheet({ user }: { user: User }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="xs">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>User Details</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Email</Label>
            <p className="flex items-center gap-2">
              {user.email}
              {user.is_admin ? <Badge variant="secondary">Admin</Badge> : null}
            </p>
          </div>
          <div>
            <Label>Name</Label>
            <p>{user.name || <None />}</p>
          </div>
          <div>
            <Label>Subscription Status</Label>
            <p>
              {user.subscription_status ? (
                <SubscriptionStatusBadge status={user.subscription_status} />
              ) : (
                <None />
              )}
            </p>
          </div>
          <div>
            <Label>Stripe Customer ID</Label>
            <p>
              {user.stripe_customer_url ? (
                <a
                  href={user.stripe_customer_url}
                  className="text-primary font-semibold hover:underline"
                  target="_blank"
                >
                  {user.stripe_customer_id}
                </a>
              ) : (
                <None />
              )}
            </p>
          </div>

          <div>
            <Label>Email Verified</Label>
            <p>
              {user.email_verified ? (
                <Badge variant="secondary">Yes</Badge>
              ) : (
                <div className="flex justify-between">
                  <Badge variant="secondary">No</Badge>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      router.post(
                        `/admin/users/${user.id}/verify_email`,
                        {},
                        {
                          preserveScroll: true,
                        },
                      );
                    }}
                  >
                    Manually Verify Email
                  </Button>
                </div>
              )}
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus | null }) {
  switch (status) {
    case null:
      return null;
    case "active":
      return (
        <Badge variant="secondary" className="bg-lime-300">
          {status}
        </Badge>
      );
    case "trialing":
      return (
        <Badge variant="secondary" className="bg-lime-100">
          {status}
        </Badge>
      );
    case "incomplete":
      return (
        <Badge variant="secondary" className="bg-lime-100">
          {status}
        </Badge>
      );
    case "incomplete_expired":
      return (
        <Badge variant="secondary" className="bg-lime-100">
          {status}
        </Badge>
      );
    case "past_due":
      return (
        <Badge variant="secondary" className="bg-red-300">
          {status}
        </Badge>
      );
    case "canceled":
      return (
        <Badge variant="secondary" className="bg-red-300">
          {status}
        </Badge>
      );
    case "unpaid":
      return (
        <Badge variant="secondary" className="bg-red-300">
          {status}
        </Badge>
      );
    case "paused":
      return (
        <Badge variant="secondary" className="bg-red-300">
          {status}
        </Badge>
      );
  }
}

interface Props {
  users: User[];
  pagy: PagyMetadata;
  filters: {
    email?: string;
    subscription_status?: string;
    stripe_customer_id?: string;
    subscription_key?: string;
  };
}

export default function Component({ users, pagy, filters }: Props) {
  return (
    <AdminLayout pageName="users">
      <div className="overflow-x-auto">
        <div className="text-sm text-muted-foreground pb-4">
          <span className="text-muted-foreground">{pagy.count} rows</span>
        </div>

        <div className="py-4 flex gap-2">
          <SimpleFilter
            label="Email"
            filter={{
              type: "text",
              value: filters.email || null,
              key: "email",
              onChange: (value) => {
                router.get("/admin/users", {
                  ...filters,
                  email: value,
                });
              },
              onClear: () => {
                const filtersWithoutEmail = {
                  ...filters,
                  email: undefined,
                };
                router.get("/admin/users", filtersWithoutEmail);
              },
            }}
          />

          <SimpleFilter
            label="Subscription Status"
            filter={{
              type: "select",
              value: filters.subscription_status || null,
              options: [
                "active",
                "trialing",
                "incomplete",
                "incomplete_expired",
                "past_due",
                "canceled",
                "unpaid",
                "paused",
              ],
              key: "subscription_status",
              onChange: (value) => {
                router.get("/admin/users", {
                  ...filters,
                  subscription_status: value,
                });
              },
              onClear: () => {
                const filtersWithoutStatus = {
                  ...filters,
                  subscription_status: undefined,
                };
                router.get("/admin/users", filtersWithoutStatus);
              },
            }}
          />

          <SimpleFilter
            label="Stripe Customer"
            filter={{
              type: "text",
              value: filters.stripe_customer_id || null,
              key: "stripe_customer_id",
              onChange: (value) => {
                router.get("/admin/users", {
                  ...filters,
                  stripe_customer_id: value,
                });
              },
              onClear: () => {
                const filtersWithoutStripeCustomerId = {
                  ...filters,
                  stripe_customer_id: undefined,
                };
                router.get("/admin/users", filtersWithoutStripeCustomerId);
              },
            }}
          />

          <SimpleFilter
            label="Subscription Key"
            filter={{
              type: "text",
              value: filters.subscription_key || null,
              key: "subscription_key",
              onChange: (value) => {
                router.get("/admin/users", {
                  ...filters,
                  subscription_key: value,
                });
              },
              onClear: () => {
                const filtersWithoutSubscriptionKey = {
                  ...filters,
                  subscription_key: undefined,
                };
                router.get("/admin/users", filtersWithoutSubscriptionKey);
              },
            }}
          />
        </div>

        <div className="rounded-md border overflow-x-auto">
          <Table className="whitespace-nowrap w-full">
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Subscription Status</TableHead>
                <TableHead>Stripe Customer</TableHead>
                <TableHead>Subscription Key</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.email}
                      {user.is_admin ? <Badge variant="secondary">Admin</Badge> : null}
                    </div>
                  </TableCell>
                  <TableCell>{formatDateTimeFromISO(user.created_at)}</TableCell>
                  <TableCell>
                    <SubscriptionStatusBadge status={user.subscription_status} />
                  </TableCell>
                  <TableCell>
                    {user.stripe_customer_url ? (
                      <a
                        href={user.stripe_customer_url}
                        className="text-primary font-semibold hover:underline"
                        target="_blank"
                      >
                        {user.stripe_customer_id}
                      </a>
                    ) : (
                      <None />
                    )}
                  </TableCell>
                  <TableCell>{user.subscription_key}</TableCell>
                  <TableCell>
                    <UserDetailsSheet user={user} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="pt-2 flex justify-end">
          <PaginationButtons
            prev={{
              href: pagy.prev ? `/admin/users` : null,
              data: { ...filters, page: pagy.prev },
            }}
            next={{
              href: pagy.next ? `/admin/users` : null,
              data: { ...filters, page: pagy.next },
            }}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
